import { Check, Link2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { TimeSlot } from '@/types';

interface TimeSlotDetailDialogProps {
  timeSlot: TimeSlot | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeZone?: string;
  className?: string;
  // Absolute URL that reopens this slot; the "Copiar enlace" button only shows with it.
  shareUrl?: string;
}

function formatDateTime(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('es-CL', {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone,
  }).format(date);
}

function formatTime(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('es-CL', {
    timeStyle: 'short',
    timeZone,
  }).format(date);
}

export function TimeSlotDetailDialog({
  timeSlot,
  open,
  onOpenChange,
  timeZone = 'America/Santiago',
  className,
  shareUrl,
}: TimeSlotDetailDialogProps) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ type: 'error', description: 'No se pudo copiar el enlace' });
    }
  };

  if (!timeSlot) {
    return null;
  }

  const startTime = new Date(timeSlot.startTime);
  const endTime = new Date(timeSlot.endTime);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-2xl border-2', className)}
        style={{ borderColor: timeSlot.color }}
      >
        <DialogHeader>
          <div
            className="mb-4 rounded-lg p-6"
            style={{ backgroundColor: timeSlot.color }}
          >
            <DialogTitle className="font-normal font-sans text-2xl text-background md:text-3xl">
              {timeSlot.title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <h4 className="font-semibold font-title text-muted-foreground text-sm uppercase">
              Horario
            </h4>
            <div className="space-y-1">
              <p className="font-mono text-sm">
                <span className="font-semibold">Inicio:</span>{' '}
                {formatDateTime(startTime, timeZone)}
              </p>
              <p className="font-mono text-sm">
                <span className="font-semibold">Fin:</span>{' '}
                {formatTime(endTime, timeZone)}
              </p>
            </div>
          </div>

          {timeSlot.location && (
            <div className="space-y-2">
              <h4 className="font-semibold font-title text-muted-foreground text-sm uppercase">
                Lugar
              </h4>
              <p className="font-mono text-sm">{timeSlot.location}</p>
            </div>
          )}

          {timeSlot.description && (
            <div className="space-y-2">
              <h4 className="font-semibold font-title text-muted-foreground text-sm uppercase">
                Descripción
              </h4>
              <p className="whitespace-pre-line font-mono text-sm leading-relaxed">
                {timeSlot.description}
              </p>
            </div>
          )}

          {shareUrl && (
            <Button type="button" variant="outline" onClick={copyLink}>
              {copied ? <Check /> : <Link2 />}
              {copied ? 'Enlace copiado' : 'Copiar enlace'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
